import type { RewardBundle, SaveData } from '../core/types';
import { quests, stages } from '../data/content';
import { WalletService } from './WalletService';

export class RewardService {
  constructor(private readonly save: SaveData) {}

  claimStage(stageId: string, stars: number): RewardBundle {
    const stage = stages.find((item) => item.id === stageId);
    if (!stage) throw new Error('Unknown stage');
    const first = !this.save.claimedFirstRewards.includes(stageId);
    const reward = first ? stage.firstReward : stage.farmReward;
    if (first) this.save.claimedFirstRewards.push(stageId);
    this.grant(reward);
    this.save.stageStars[stageId] = Math.max(this.save.stageStars[stageId] ?? 0, Math.max(1, Math.min(3, stars)));
    this.save.victories += 1;
    const stageIndex = stages.findIndex((item) => item.id === stageId);
    this.save.unlockedStage = Math.min(stages.length, Math.max(this.save.unlockedStage, stageIndex + 2));
    this.progress('battles', 1);
    return reward;
  }

  claimQuest(questId: string): RewardBundle {
    const quest = quests.find((item) => item.id === questId);
    if (!quest) throw new Error('Unknown quest');
    const state = this.save.quests[questId] ?? { progress: 0, claimed: false };
    if (state.claimed) throw new Error('Reward already claimed');
    if (state.progress < quest.target) throw new Error('Quest is not complete');
    state.claimed = true; this.save.quests[questId] = state;
    this.grant(quest.reward);
    return quest.reward;
  }

  progress(metric: string, amount: number): void {
    for (const quest of quests.filter((item) => item.metric === metric)) {
      const state = this.save.quests[quest.id] ?? { progress: 0, claimed: false };
      state.progress = Math.min(quest.target, state.progress + Math.max(0, Math.floor(amount)));
      this.save.quests[quest.id] = state;
    }
  }

  redeemCode(code: string, now = Date.now()): RewardBundle {
    const normalized = code.trim().toUpperCase();
    const codes: Record<string, { reward: RewardBundle; expires?: number }> = {
      LAUNCHDAY: { reward: { gems: 300, gold: 5000 } },
      ASTRAL100K: { reward: { tickets: 5, expCrystals: 20 } },
      SHATTEREDREALMS: { reward: { traitTokens: 6, ascensionCrystals: 5 } },
      WELCOMEKEEPER: { reward: { gems: 200, gold: 2500, expCrystals: 10 } },
    };
    const entry = codes[normalized];
    if (!entry) throw new Error('Invalid gift code');
    if (entry.expires && now > entry.expires) throw new Error('Gift code expired');
    if (this.save.claimedCodes.includes(normalized)) throw new Error('Gift code already used');
    this.save.claimedCodes.push(normalized); this.grant(entry.reward);
    return entry.reward;
  }

  grant(reward: RewardBundle): void {
    new WalletService(this.save.wallet).grant(reward);
    this.save.playerExp += Math.max(0, Math.floor(reward.playerExp ?? 0));
    while (this.save.playerExp >= this.save.playerLevel * 500) { this.save.playerExp -= this.save.playerLevel * 500; this.save.playerLevel += 1; }
  }
}
